import { contentRefFromLegacyPath, contentRefWebPath } from "@compass/shared/utils/contentLinks";
import { useNavigate } from "@tanstack/react-router";
import React from "react";
import type { ProseRenderConfig } from "@compass/shared/components/TipTapRenderer";
import { MarkdownTable } from "@compass/shared/components/charts";

const InternalLink: React.FC<{ to: string; children: React.ReactNode; title?: string | null }> = ({
    to,
    children,
    title,
}) => {
    const navigate = useNavigate();
    return (
        <a
            href={to}
            title={title ?? undefined}
            className="text-brand-alice hover:text-brand-azure hover:!underline"
            onClick={(e) => {
                // Let the browser handle modifier-clicks (cmd/ctrl/shift/alt)
                // and non-primary buttons so cmd-click → new tab, middle-click,
                // and right-click context menu still work as expected.
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
                e.preventDefault();
                navigate({ to } as unknown as Parameters<typeof navigate>[0]);
            }}
        >
            {children}
        </a>
    );
};

export const webProseComponents: ProseRenderConfig = {
    Paragraph: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
    Blockquote: ({ children }) => (
        <blockquote className="ml-0 mb-3 border-0 border-l-8 border-solid border-l-gray-200 bg-[#f8f8f9] py-1 pl-2 italic text-gray-600">
            {children}
        </blockquote>
    ),
    BulletList: ({ children }) => (
        <ul className="ml-6 mb-3 list-disc whitespace-normal">{children}</ul>
    ),
    OrderedList: ({ start, children }) => (
        <ol start={start} className="ml-6 mb-3 list-decimal whitespace-normal">
            {children}
        </ol>
    ),
    ListItem: ({ children }) => <li className="ml-2">{children}</li>,
    Code: ({ children }) => <code className="text-pink-600">{children}</code>,
    Link: ({ href, parsed, title, children }) => {
        if (parsed.kind === "unsafe") {
            return (
                <span title={title ?? undefined} data-unsafe-link={href}>
                    {children}
                </span>
            );
        }
        if (parsed.kind === "internal") {
            // A pre-PRTL-981 saved answer may hold a path from the other
            // app's route table (/articles/post/x); resolve it to ours.
            const legacy = contentRefFromLegacyPath(parsed.path);
            return (
                <InternalLink to={legacy ? contentRefWebPath(legacy) : parsed.path} title={title}>
                    {children}
                </InternalLink>
            );
        }
        // PRTL-981: platform content cited in its own scheme (article:123).
        if (parsed.kind === "content") {
            return (
                <InternalLink to={contentRefWebPath(parsed.ref)} title={title}>
                    {children}
                </InternalLink>
            );
        }
        return (
            <a
                href={href}
                title={title ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-alice hover:text-brand-azure hover:!underline"
            >
                {children}
            </a>
        );
    },
    Table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
};
